<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import AgentsAPI from 'dashboard/api/agents';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentVoiceProfileForm from 'dashboard/routes/dashboard/settings/agents/components/AgentVoiceProfileForm.vue';
import AgentSection from './AgentSection.vue';

const props = defineProps({
  canManage: { type: Boolean, default: false },
});

const { t } = useI18n();
const humanAgents = ref([]);
const selectedAgentId = ref(null);
const isLoading = ref(true);
const loadError = ref(false);

const selectedAgent = computed(() =>
  humanAgents.value.find(agent => agent.id === selectedAgentId.value)
);

const agentLabel = agent => {
  const name = agent.available_name || agent.name || agent.email;
  return agent.email && agent.email !== name
    ? `${name} (${agent.email})`
    : name;
};

const loadAgents = async () => {
  if (!props.canManage) return;

  isLoading.value = true;
  loadError.value = false;
  try {
    const { data } = await AgentsAPI.get();
    humanAgents.value = Array.isArray(data) ? data : [];
    if (
      selectedAgentId.value &&
      !humanAgents.value.some(agent => agent.id === selectedAgentId.value)
    ) {
      selectedAgentId.value = null;
    }
  } catch (error) {
    humanAgents.value = [];
    selectedAgentId.value = null;
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadAgents);
</script>

<template>
  <section id="agent-voice-profile" class="flex flex-col gap-6" tabindex="-1">
    <template v-if="canManage">
      <AgentSection
        :title="t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.TITLE')"
        prefix-icon="i-lucide-mic-vocal"
      >
        <div
          v-if="isLoading"
          class="flex min-h-48 flex-col items-center justify-center gap-3 text-center"
          data-testid="voice-agents-loading"
          role="status"
          aria-live="polite"
        >
          <span
            class="i-lucide-loader-circle size-6 animate-spin text-n-blue-11"
            aria-hidden="true"
          />
          <p class="text-sm text-n-slate-11">
            {{ t('AGENT_MGMT.LOADING') }}
          </p>
        </div>

        <div
          v-else-if="loadError"
          class="flex min-h-48 flex-col items-center justify-center gap-4 rounded-xl border border-n-ruby-5 bg-n-ruby-2 px-6 py-8 text-center"
          data-testid="voice-agents-error"
          role="alert"
        >
          <span
            class="i-lucide-circle-alert size-7 text-n-ruby-10"
            aria-hidden="true"
          />
          <p class="max-w-lg text-sm text-n-ruby-11">
            {{ t('CAPTAIN_EXPERIENCE.ALERT_LOAD_AGENTS_ERROR') }}
          </p>
          <Button
            color="ruby"
            variant="outline"
            icon="i-lucide-refresh-cw"
            :label="t('CAPTAIN_EXPERIENCE.WORKSPACE.RETRY')"
            @click="loadAgents"
          />
        </div>

        <div
          v-else-if="!humanAgents.length"
          class="flex min-h-48 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-n-weak bg-n-alpha-2 px-6 py-8 text-center"
          data-testid="voice-agents-empty"
        >
          <span
            class="i-lucide-users-round size-8 text-n-slate-9"
            aria-hidden="true"
          />
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('AGENT_MGMT.LIST.404') }}
          </h3>
          <p class="max-w-lg text-sm leading-relaxed text-n-slate-11">
            {{ t('AGENT_MGMT.DESCRIPTION') }}
          </p>
        </div>

        <div v-else class="grid gap-5">
          <div class="grid gap-2">
            <label
              for="voice-profile-agent"
              class="text-sm font-medium text-n-slate-12"
            >
              {{ t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT') }}
            </label>
            <p
              id="voice-profile-agent-description"
              class="text-sm text-n-slate-11"
            >
              {{ t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.DESCRIPTION') }}
            </p>
            <div class="relative max-w-2xl">
              <select
                id="voice-profile-agent"
                v-model="selectedAgentId"
                class="h-11 w-full appearance-none rounded-xl border border-n-weak bg-n-solid-2 px-3 pr-10 text-sm text-n-slate-12 outline-none transition hover:border-n-strong focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
                aria-describedby="voice-profile-agent-description"
              >
                <option :value="null">
                  {{ t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER') }}
                </option>
                <option
                  v-for="agent in humanAgents"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agentLabel(agent) }}
                </option>
              </select>
              <span
                class="i-lucide-chevrons-up-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
                aria-hidden="true"
              />
            </div>
          </div>

          <div
            v-if="!selectedAgent"
            class="flex min-h-36 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-n-weak bg-n-alpha-2 px-6 py-8 text-center"
            data-testid="voice-profile-unselected"
          >
            <span
              class="i-lucide-audio-lines size-8 text-n-slate-9"
              aria-hidden="true"
            />
            <h3 class="text-base font-medium text-n-slate-12">
              {{ t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT') }}
            </h3>
            <p class="max-w-lg text-sm leading-relaxed text-n-slate-11">
              {{ t('AGENT_MGMT.EDIT.FORM.VOICE_PROFILE.DESCRIPTION') }}
            </p>
          </div>

          <div v-else class="grid gap-3" data-testid="voice-profile-selected">
            <p class="text-sm font-medium text-n-slate-12" aria-live="polite">
              {{ agentLabel(selectedAgent) }}
            </p>
            <AgentVoiceProfileForm
              :key="selectedAgentId"
              :agent-id="selectedAgentId"
            />
          </div>
        </div>
      </AgentSection>
    </template>
  </section>
</template>
